import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import { Plug, Loader2, CheckCircle2, XCircle, FlaskConical, PlugZap, History, FileText } from 'lucide-react';

const CONN_STATUSES = ['request_submitted', 'under_review', 'info_required', 'approved', 'awaiting_authorization', 'awaiting_credentials', 'configuring', 'testing', 'connected', 'connection_error', 'paused', 'disconnected', 'declined'];
const badgeColor = (s) => {
  if (s === 'connected') return 'bg-emerald-100 text-emerald-700';
  if (['connection_error', 'paused', 'disconnected', 'declined'].includes(s)) return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

const AdminTabATS = () => {
  const [reqs, setReqs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [busy, setBusy] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [statusPick, setStatusPick] = useState('');
  const [acctId, setAcctId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await axios.get(`${API_URL}/admin/ats/requests`, { params });
      setReqs(res.data.requests); setCounts(res.data.counts);
    } catch { toast.error('Failed to load ATS requests'); } finally { setLoading(false); }
  }, [filterStatus]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (r) => {
    setDetail(r);
    setDetailData(null);
    setAdminNotes(r.admin_notes || '');
    setStatusPick(r.integration?.status || r.status);
    setAcctId(r.integration?.ats_account_identifier || '');
    try {
      const res = await axios.get(`${API_URL}/admin/ats/requests/${r.id}`);
      setDetailData(res.data);
    } catch { /* ignore */ }
  };

  const act = async (fn, okMsg) => {
    setBusy(okMsg);
    try { await fn(); toast.success(okMsg); await load(); if (detail) await openDetail(detail); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  const approve = () => act(() => axios.post(`${API_URL}/admin/ats/requests/${detail.id}/approve`), 'Approved');
  const decline = () => act(() => axios.put(`${API_URL}/admin/ats/requests/${detail.id}`, { status: 'declined', decline_reason: declineReason }), 'Declined');
  const saveNotes = () => act(() => axios.put(`${API_URL}/admin/ats/requests/${detail.id}`, { admin_notes: adminNotes }), 'Notes saved');
  const reqInfo = () => act(() => axios.put(`${API_URL}/admin/ats/requests/${detail.id}`, { status: 'info_required', admin_notes: adminNotes }), 'Info requested');

  const integ = detailData?.integration;
  const setIntegStatus = (extra = {}) => act(() => axios.post(`${API_URL}/admin/ats/integrations/${integ.id}/status`, { status: statusPick, ats_account_identifier: acctId, ...extra }), 'Integration updated');
  const setBilling = (field, value) => act(() => axios.post(`${API_URL}/admin/ats/integrations/${integ.id}/status`, { [field]: value }), 'Billing updated');
  const testConn = () => act(() => axios.post(`${API_URL}/admin/ats/integrations/${integ.id}/test-connection`), 'Test connection run (test mode)');
  const testApp = () => act(() => axios.post(`${API_URL}/admin/ats/integrations/${integ.id}/test-application`), 'Test application run (test mode)');

  return (
    <div className="space-y-6" data-testid="admin-ats-tab">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Plug className="w-5 h-5 text-primary" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">ATS Integration Requests</h3>
          <p className="text-sm text-gray-500">Review, approve, configure, and test employer ATS integrations. Live delivery activates per-employer only after a successful test (Phase 3).</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['total', 'Total'], ['pending', 'Pending'], ['connected', 'Connected'], ['errors', 'Errors']].map(([k, l]) => (
          <div key={k} className="bg-white rounded-lg border border-gray-100 p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{counts[k] ?? 0}</div>
            <div className="text-xs text-gray-500">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-sm">Filter status</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-56" data-testid="ats-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {CONN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
          : reqs.length === 0 ? <div className="p-10 text-center text-gray-500">No ATS requests yet.</div>
          : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead><tr><th>Company</th><th>Provider</th><th>Requested</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {reqs.map((r) => (
                    <tr key={r.id} data-testid={`ats-req-row-${r.id}`}>
                      <td className="font-medium">{r.company_name}</td>
                      <td>{r.provider}</td>
                      <td>{r.created_at?.slice(0, 10)}</td>
                      <td><Badge className={badgeColor(r.integration?.status || r.status)}>{r.integration?.status || r.status}</Badge></td>
                      <td><Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`ats-review-${r.id}`}>Review</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDetailData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="font-serif">{detail?.company_name} — {detail?.provider}</DialogTitle></DialogHeader>
          {!detail || !detailData ? <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div> : (
            <div className="space-y-5">
              {/* Request info */}
              <div className="grid sm:grid-cols-2 gap-2 text-sm bg-gray-50 rounded-lg p-3">
                <div><span className="text-gray-500">Contact:</span> {detail.contact_name} ({detail.contact_email})</div>
                <div><span className="text-gray-500">Phone:</span> {detail.contact_phone || '—'}</div>
                <div><span className="text-gray-500">ATS Admin:</span> {detail.ats_admin_name} ({detail.ats_admin_email})</div>
                <div><span className="text-gray-500">Active jobs:</span> {detail.active_job_count ?? '—'} · Locations: {detail.location_count ?? '—'}</div>
                <div><span className="text-gray-500">Resume:</span> {detail.resume_required ? 'Yes' : 'No'} · Cover: {detail.cover_letter_required ? 'Yes' : 'No'} · Custom Q: {detail.custom_questions_required ? 'Yes' : 'No'}</div>
                <div><span className="text-gray-500">Authorization:</span> {detail.authorization_accepted ? '✓ Accepted' : 'Not accepted'}</div>
                {detail.employer_notes && <div className="sm:col-span-2"><span className="text-gray-500">Notes:</span> {detail.employer_notes}</div>}
              </div>

              {/* Admin notes */}
              <div>
                <Label className="text-xs">Internal admin notes</Label>
                <Textarea rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} data-testid="ats-admin-notes" />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={saveNotes} disabled={!!busy}>Save notes</Button>
                  <Button size="sm" variant="outline" onClick={reqInfo} disabled={!!busy}>Request more info</Button>
                </div>
              </div>

              {/* Approve / decline */}
              {!integ && detail.status !== 'declined' && (
                <div className="flex flex-wrap items-end gap-2 border-t pt-4">
                  <Button onClick={approve} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ats-approve-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" />Approve
                  </Button>
                  <div className="flex-1 min-w-[200px]">
                    <Input placeholder="Decline reason" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                  </div>
                  <Button onClick={decline} disabled={!!busy} variant="outline" className="text-red-600 border-red-200" data-testid="ats-decline-btn">
                    <XCircle className="w-4 h-4 mr-2" />Decline
                  </Button>
                </div>
              )}

              {/* Integration config */}
              {integ && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2"><PlugZap className="w-4 h-4" />Integration configuration</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Connection status</Label>
                      <Select value={statusPick} onValueChange={setStatusPick}>
                        <SelectTrigger data-testid="ats-integ-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{CONN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">ATS account identifier (masked)</Label>
                      <Input value={acctId} onChange={(e) => setAcctId(e.target.value)} placeholder="e.g. board token" data-testid="ats-acct-id" />
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setIntegStatus()} disabled={!!busy} className="bg-primary hover:bg-primary-hover text-white" data-testid="ats-save-status-btn">Save status & identifier</Button>

                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center justify-between border rounded-lg p-2">
                      <span>Setup fee ($299): <strong>{integ.setup_fee_status}</strong></span>
                      <Button size="sm" variant="outline" onClick={() => setBilling('setup_fee_status', integ.setup_fee_status === 'active' ? 'inactive' : 'active')} data-testid="ats-setup-fee-btn">
                        {integ.setup_fee_status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between border rounded-lg p-2">
                      <span>Add-on ($99/mo): <strong>{integ.recurring_addon_status}</strong></span>
                      <Button size="sm" variant="outline" onClick={() => setBilling('recurring_addon_status', integ.recurring_addon_status === 'active' ? 'inactive' : 'active')} data-testid="ats-addon-btn">
                        {integ.recurring_addon_status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">Billing flags are admin-controlled. Live Stripe charging + real ATS submission activate in Phase 2/3.</p>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={testConn} disabled={!!busy} data-testid="ats-test-conn-btn"><FlaskConical className="w-4 h-4 mr-2" />Test connection</Button>
                    <Button size="sm" variant="outline" onClick={testApp} disabled={!!busy} data-testid="ats-test-app-btn"><FlaskConical className="w-4 h-4 mr-2" />Test application</Button>
                  </div>
                </div>
              )}

              {/* Submission logs */}
              {detailData.submission_logs?.length > 0 && (
                <div className="border-t pt-3">
                  <div className="flex items-center gap-2 text-sm font-medium mb-2"><FileText className="w-4 h-4" />Submission logs</div>
                  <div className="text-xs text-gray-500 space-y-1 max-h-28 overflow-y-auto">
                    {detailData.submission_logs.map((l) => <div key={l.id}>{l.submitted_at?.slice(0, 16).replace('T', ' ')} — {l.submission_status} {l.failure_reason || ''}</div>)}
                  </div>
                </div>
              )}

              {/* Activity */}
              {detailData.activity?.length > 0 && (
                <div className="border-t pt-3">
                  <div className="flex items-center gap-2 text-sm font-medium mb-2"><History className="w-4 h-4" />Activity log</div>
                  <div className="text-xs text-gray-500 space-y-1 max-h-32 overflow-y-auto">
                    {detailData.activity.map((a) => <div key={a.id}>{a.created_at?.slice(0, 16).replace('T', ' ')} — <strong>{a.action}</strong> {a.previous_status ? `(${a.previous_status}→${a.new_status})` : ''} {a.description}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTabATS;
